import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import ProfileEditor from "./ProfileEditor";
import { StandardFramingConfigurator } from "../components/StandardFramingConfigurator";
import { buildStandardFramingOptions } from "../lib/framing";

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-neutral-200 p-6">
      <div className="overline text-neutral-500">{label}</div>
      <div className="font-serif text-4xl mt-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

const createLightPreview = async (file, maxEdge = 1200, quality = 0.72) => {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return canvas.toDataURL("image/webp", quality);
};

function NotificationsPanel() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = async () => { try { await api.post("/notifications/mark-all-read"); load(); } catch (e) { /* ignore */ } };
  if (data.items.length === 0) return null;
  return (
    <div className="bg-white border border-neutral-200 p-6 mt-4" data-testid="notifications-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="overline text-neutral-700">Notificaciones {data.unread > 0 && <span className="ml-2 bg-black text-white text-[10px] px-2 py-0.5">{data.unread}</span>}</div>
        {data.unread > 0 && <button onClick={markRead} className="text-xs text-neutral-500 hover:text-black underline" data-testid="mark-read">Marcar todas como leídas</button>}
      </div>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {data.items.slice(0, 8).map((n) => (
          <div key={n.id} className={`border-l-2 pl-4 py-1 ${n.read ? "border-neutral-200" : (n.type === "artwork_rejected" ? "border-red-500" : "border-emerald-500")}`} data-testid={`notif-${n.id}`}>
            <div className="text-sm font-medium">{n.title}</div>
            <div className="text-xs text-neutral-600 mt-1 whitespace-pre-wrap">{n.body}</div>
            <div className="text-[10px] text-neutral-400 mt-1 uppercase tracking-wider">{new Date(n.created_at).toLocaleDateString()} {new Date(n.created_at).toLocaleTimeString().slice(0,5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Overview() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/artist").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="overline text-neutral-400">Loading...</div>;
  return (
    <div data-testid="artist-overview">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-8">{t.artist.overview}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t.artist.works} value={stats.total_works} sub={`${stats.approved} ${t.artist.approved.toLowerCase()}, ${stats.pending} ${t.artist.pending.toLowerCase()}`} />
        <StatCard label={t.artist.units} value={stats.units_sold} />
        <StatCard label={t.artist.revenue} value={`€${stats.revenue.toFixed(2)}`} />
        <StatCard label={t.artist.netEarned} value={`€${stats.net_earned.toFixed(2)}`} sub={t.artist.netSub} />
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="bg-emerald-50 border border-emerald-200 p-6" data-testid="net-paid-card">
          <div className="overline text-[10px] text-emerald-700">Cobrado</div>
          <div className="font-serif text-3xl mt-2 text-emerald-700">€{(stats.net_paid || 0).toFixed(2)}</div>
          <div className="text-xs text-emerald-700/70 mt-1">Liquidado por la galería</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 p-6" data-testid="net-pending-card">
          <div className="overline text-[10px] text-amber-700">Pendiente de cobro</div>
          <div className="font-serif text-3xl mt-2 text-amber-700">€{(stats.net_pending || 0).toFixed(2)}</div>
          <div className="text-xs text-amber-700/70 mt-1">Pendiente de la próxima liquidación</div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <StatCard label={t.artist.totalViews} value={stats.total_views} />
        <div className="bg-white border border-neutral-200 p-6 lg:col-span-2" data-testid="top-viewed-card">
          <div className="overline text-neutral-500 mb-3">{t.artist.topViewed}</div>
          {stats.top_viewed.length === 0 ? (
            <div className="text-sm text-neutral-400">—</div>
          ) : (
            <div className="space-y-2">
              {stats.top_viewed.map((tv) => (
                <div key={tv.id} className="flex justify-between items-center text-sm py-1">
                  <span className="truncate pr-3">{tv.title}</span>
                  <span className="font-mono text-neutral-500">{tv.views} {t.artist.viewsUnit}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <NotificationsPanel />
    </div>
  );
}

function ContextImagesModal({ artwork, onClose, onSaved }) {
  const [images, setImages] = useState(artwork.context_images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (images.length + files.length > 6) {
      toast.error("Máximo 6 imágenes de contexto");
      return;
    }
    setUploading(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.path);
      }
      setImages((prev) => [...prev, ...added]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/artworks/${artwork.id}`, { context_images: images });
      toast.success("Imágenes de contexto actualizadas");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="context-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Imágenes de contexto · {artwork.title}</DialogTitle>
          <DialogDescription>
            Sube hasta 6 fotos de la obra colgada en un entorno real (casa, oficina, sala de espera…). Ayudan al comprador a imaginarla en su espacio.
          </DialogDescription>
        </DialogHeader>

        {images.length > 0 ? (
          <div className="grid grid-cols-3 gap-3" data-testid="context-modal-grid">
            {images.map((path, idx) => (
              <div key={idx} className="relative group aspect-[4/3] bg-neutral-100 overflow-hidden border border-neutral-200" data-testid={`ctx-modal-img-${idx}`}>
                <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="absolute top-1 right-1 bg-black/80 text-white text-xs w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`ctx-modal-remove-${idx}`}
                >×</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 overline text-neutral-400 border border-dashed border-neutral-200">
            Sin imágenes de contexto todavía
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-neutral-100 mt-2">
          {images.length < 6 ? (
            <label className="inline-block cursor-pointer">
              <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" disabled={uploading} data-testid="ctx-modal-input" />
              <span className="inline-block overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50">
                {uploading ? "Subiendo..." : `+ Añadir (${images.length}/6)`}
              </span>
            </label>
          ) : <span className="text-xs text-neutral-500">Límite alcanzado (6/6)</span>}

          <div className="flex gap-2">
            <button onClick={onClose} className="overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50" data-testid="ctx-modal-cancel">Cancelar</button>
            <button onClick={save} disabled={saving} className="overline text-xs px-4 py-2 bg-black text-white hover:bg-neutral-800 disabled:opacity-50" data-testid="ctx-modal-save">
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function EditArtworkModal({ artwork, onClose, onSaved }) {
  const [title, setTitle] = useState(artwork.title);
  const [description, setDescription] = useState(artwork.description);
  const [category, setCategory] = useState(artwork.category);
  const [sizes, setSizes] = useState(
    (artwork.sizes || []).map((s) => ({
      label: s.label,
      width_cm: s.width_cm,
      height_cm: s.height_cm,
      net_earnings: s.net_earnings ?? Math.max(0, (s.price || 0) * 0.5),
    }))
  );
  const [newImagePath, setNewImagePath] = useState(null);
  const [newImageInfo, setNewImageInfo] = useState(null);
  const [newPreview, setNewPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ print_rate_per_m2: 55, print_min: 25, gallery_pct: 0.20, referrer_pct: 0.10 });
  const [standardFraming, setStandardFraming] = useState(() => buildStandardFramingOptions(artwork.image_width, artwork.image_height, artwork.standard_framing || []));

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const onNewFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      setNewPreview(await createLightPreview(f));
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewImagePath(data.path);
      setNewImageInfo(data);
      setStandardFraming(buildStandardFramingOptions(data.width_px, data.height_px, standardFraming));
    } catch (err) { toast.error(formatApiError(err)); setNewPreview(null); }
    finally { setUploading(false); }
  };

  const updateSize = (idx, field, value) => {
    setSizes((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value === "" ? "" : parseFloat(value) } : s));
  };
  const addSize = () => setSizes((prev) => [...prev, { label: `Custom${prev.length + 1}`, width_cm: 40, height_cm: 30, net_earnings: 80 }]);
  const removeSize = (idx) => setSizes((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!title.trim()) { toast.error("El título no puede estar vacío"); return; }
    if (sizes.length === 0) { toast.error("Debe haber al menos una talla"); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        category,
        sizes: sizes.map((s) => ({
          label: s.label,
          width_cm: parseFloat(s.width_cm),
          height_cm: parseFloat(s.height_cm),
          net_earnings: parseFloat(s.net_earnings) || 0,
        })),
        standard_framing: standardFraming.map((o) => ({
          code: o.code,
          enabled: !!o.enabled,
          net_earnings: parseFloat(o.net_earnings) || 0,
          crop_x: parseFloat(o.crop_x ?? 0.5),
          crop_y: parseFloat(o.crop_y ?? 0.5),
        })),
      };
      if (newImagePath) payload.image_path = newImagePath;
      const { data } = await api.patch(`/artworks/${artwork.id}`, payload);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const wasApproved = artwork.status === "approved";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Editar · {artwork.title}</DialogTitle>
          <DialogDescription>
            Cambios en descripción y categoría se aplican al instante. Cambios en precio, tallas, título o imagen requieren <strong>re-aprobación</strong> si la obra ya estaba publicada.
          </DialogDescription>
        </DialogHeader>

        {wasApproved && (
          <div className="bg-amber-50 border-l-2 border-amber-500 p-3 text-xs text-amber-900">
            ⚠️ Esta obra está publicada. Si modificas el precio, tallas, título o el archivo, volverá a <strong>pendiente</strong> hasta que admin la revise.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="overline text-[10px] text-neutral-500 mb-1 block">Título</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-neutral-300 px-3 py-2 text-sm" data-testid="edit-title" />
          </div>
          <div>
            <label className="overline text-[10px] text-neutral-500 mb-1 block">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full border border-neutral-300 px-3 py-2 text-sm" data-testid="edit-description" />
          </div>
          <div>
            <label className="overline text-[10px] text-neutral-500 mb-1 block">Categoría</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="border border-neutral-300 px-3 py-2 text-sm bg-white" data-testid="edit-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <div className="overline text-[10px] text-neutral-500 mb-2">Archivo original</div>
            <div className="flex gap-3 items-center">
              <img src={newPreview || fileUrl(artwork.image_path)} alt="" className="w-24 h-24 object-cover bg-neutral-100" />
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={onNewFile} className="hidden" disabled={uploading} data-testid="edit-image-input" />
                <span className="inline-block overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50">
                  {uploading ? "Subiendo..." : (newImagePath ? "Reemplazar de nuevo" : "Reemplazar archivo")}
                </span>
              </label>
              {newImagePath && <span className="overline text-[10px] text-emerald-700">Nueva imagen lista</span>}
            </div>
            {newImageInfo && (
              <div className="text-[10px] text-neutral-500 mt-1" data-testid="edit-image-info">
                {newImageInfo.width_px}×{newImageInfo.height_px}px · ratio {newImageInfo.aspect_ratio}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-[10px] text-neutral-500">Tallas y precios netos</div>
              <button onClick={addSize} className="overline text-[10px] px-2 py-1 border border-neutral-300 hover:bg-neutral-50" data-testid="edit-add-size">+ Añadir talla</button>
            </div>
            <div className="space-y-2">
              {sizes.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-neutral-50 p-2 border border-neutral-200" data-testid={`edit-size-${i}`}>
                  <input value={s.label} onChange={(e) => setSizes((prev) => prev.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} className="col-span-2 border border-neutral-300 px-2 py-1.5 text-xs" placeholder="Talla" />
                  <input type="number" min="1" value={s.width_cm} onChange={(e) => updateSize(i, "width_cm", e.target.value)} className="col-span-2 border border-neutral-300 px-2 py-1.5 text-xs" placeholder="W cm" />
                  <input type="number" min="1" value={s.height_cm} onChange={(e) => updateSize(i, "height_cm", e.target.value)} className="col-span-2 border border-neutral-300 px-2 py-1.5 text-xs" placeholder="H cm" />
                  <div className="col-span-5 flex items-center gap-2">
                    <span className="text-[10px] text-neutral-500">Neto €</span>
                    <input type="number" min="0" step="0.5" value={s.net_earnings} onChange={(e) => updateSize(i, "net_earnings", e.target.value)} className="flex-1 border border-neutral-300 px-2 py-1.5 text-xs" placeholder="Neto €" />
                  </div>
                  <button onClick={() => removeSize(i)} className="col-span-1 text-red-600 text-sm">×</button>
                </div>
              ))}
            </div>
          </div>

          {standardFraming.length > 0 && (
            <StandardFramingConfigurator
              imageSrc={newPreview || fileUrl(artwork.image_path, "sm")}
              options={standardFraming}
              onChange={setStandardFraming}
              settings={settings}
              testPrefix="edit-standard-framing"
            />
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 mt-4">
          <button onClick={onClose} className="overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50" data-testid="edit-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="overline text-xs px-6 py-2 bg-black text-white hover:bg-neutral-800 disabled:opacity-50" data-testid="edit-save">
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function MyWorks() {
  const { t } = useI18n();
  const [works, setWorks] = useState([]);
  const [ctxEditing, setCtxEditing] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/artworks/mine").then((r) => setWorks(r.data)).catch(() => setWorks([]));
  useEffect(() => { load(); }, []);

  const statusMap = {
    pending: { color: "bg-amber-50 text-amber-700 border-amber-200", label: "Pendiente" },
    approved: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Publicada" },
    rejected: { color: "bg-red-50 text-red-700 border-red-200", label: "Rechazada" },
    archived: { color: "bg-neutral-100 text-neutral-600 border-neutral-300", label: "Archivada" },
    sold_out: { color: "bg-purple-50 text-purple-700 border-purple-200", label: "Agotada" },
    sold_exclusive: { color: "bg-blue-50 text-blue-700 border-blue-200", label: "Exclusiva" },
  };

  const archive = async (w) => {
    if (!window.confirm(`¿Retirar «${w.title}» del catálogo?`)) return;
    try { await api.patch(`/artworks/${w.id}/archive`); load(); toast.success("Obra retirada"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const republish = async (w) => {
    try {
      const { data } = await api.patch(`/artworks/${w.id}/republish`);
      load();
      toast.success(data.status === "approved" ? "Obra publicada" : "Enviada para revisión");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (w) => {
    if (!window.confirm(`¿Eliminar «${w.title}» definitivamente?`)) return;
    try { await api.delete(`/artworks/${w.id}`); load(); toast.success("Obra eliminada"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="artist-works">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-2">{t.artist.works}</h1>
      <p className="text-sm text-neutral-500 mb-8">Pulsa <strong>Editar</strong> para cambiar precio, tallas o el archivo. Cambios mayores en obras publicadas volverán a pendiente de aprobación.</p>

      {works.length === 0 ? (
        <div className="bg-white border border-neutral-200 py-16 text-center overline text-neutral-400">Aún no has subido obras</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="works-grid">
          {works.map((w) => {
            const sizes = w.sizes || [];
            const retails = sizes.map((s) => s.price);
            const minR = retails.length ? Math.min(...retails) : 0;
            const maxR = retails.length ? Math.max(...retails) : 0;
            const totalNet = sizes.reduce((s, x) => s + (x.net_earnings || 0), 0);
            const st = statusMap[w.status] || statusMap.pending;
            return (
              <div key={w.id} className="bg-white border border-neutral-200 flex flex-col" data-testid={`work-card-${w.id}`}>
                <div className="bg-neutral-100 flex items-center justify-center" style={{ minHeight: "280px" }}>
                  <img src={fileUrl(w.image_path)} alt={w.title} className="max-w-full max-h-[420px] w-auto h-auto object-contain" loading="lazy" />
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`overline text-[10px] px-2 py-1 border ${st.color}`}>{st.label}</span>
                    <span className="overline text-[10px] text-neutral-400">{w.views || 0} vistas</span>
                  </div>
                  <h3 className="font-serif text-xl leading-tight">{w.title}</h3>
                  <div className="text-xs text-neutral-500 mt-1">{w.category}</div>
                  {sizes.length > 0 && (
                    <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                      {sizes.map((s) => `${s.label} ${s.width_cm}×${s.height_cm}`).join(" · ")} cm
                    </div>
                  )}
                  <div className="text-[10px] text-neutral-500 mt-1 uppercase tracking-wider">
                    {w.max_impressions ? `Edición ${w.prints_sold || 0}/${w.max_impressions}` : `Edición abierta · ${w.prints_sold || 0} vendidas`}
                  </div>
                  <div className="flex items-end justify-between mt-3 pt-3 border-t border-neutral-100">
                    <div>
                      <div className="overline text-[10px] text-neutral-500">PVP</div>
                      <div className="font-serif text-base">
                        {sizes.length > 1 ? `€${minR.toFixed(2)}–€${maxR.toFixed(2)}` : `€${minR.toFixed(2)}`}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="overline text-[10px] text-neutral-500">Neto/talla</div>
                      <div className="font-serif text-base text-emerald-700">€{totalNet.toFixed(2)}</div>
                    </div>
                  </div>
                  {w.status === "rejected" && w.rejection_message && (
                    <div className="mt-3 text-[10px] text-red-700 whitespace-pre-wrap p-2 bg-red-50 border border-red-100" data-testid={`reason-${w.id}`}>
                      {w.rejection_message}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button onClick={() => setEditing(w)} className="overline text-xs px-3 py-2 border border-black hover:bg-neutral-50" data-testid={`edit-${w.id}`}>Editar</button>
                    <button onClick={() => setCtxEditing(w)} className="overline text-xs px-3 py-2 border border-neutral-300 hover:bg-neutral-50" data-testid={`ctx-edit-${w.id}`}>
                      Contexto{w.context_images?.length ? ` (${w.context_images.length})` : ""}
                    </button>
                    {w.status === "approved" && (
                      <button onClick={() => archive(w)} className="overline text-xs px-3 py-2 border border-neutral-300 hover:bg-neutral-50" data-testid={`archive-${w.id}`}>Retirar</button>
                    )}
                    {w.status === "archived" && (
                      <button onClick={() => republish(w)} className="overline text-xs px-3 py-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid={`republish-${w.id}`}>Publicar</button>
                    )}
                    <button onClick={() => remove(w)} className="overline text-xs px-3 py-2 border border-red-300 text-red-700 hover:bg-red-50 col-span-2" data-testid={`delete-${w.id}`}>Eliminar definitivamente</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {ctxEditing && (
        <ContextImagesModal
          artwork={ctxEditing}
          onClose={() => setCtxEditing(null)}
          onSaved={() => { setCtxEditing(null); load(); }}
        />
      )}
      {editing && (
        <EditArtworkModal
          artwork={editing}
          onClose={() => setEditing(null)}
          onSaved={(res) => {
            setEditing(null);
            load();
            if (res?.requires_reapproval) {
              toast.message("Obra enviada a revisión", { description: "Los cambios mayores requieren re-aprobación del admin." });
            } else {
              toast.success("Cambios guardados");
            }
          }}
        />
      )}
    </div>
  );
}

function Upload() {
  const { t } = useI18n();
  const [form, setForm] = useState({ title: "", description: "", category: "abstracto" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [standardFraming, setStandardFraming] = useState([]);
  const [contextImages, setContextImages] = useState([]);
  const [editionMode, setEditionMode] = useState("open");
  const [maxImpressions, setMaxImpressions] = useState(25);
  const [contextUploading, setContextUploading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ print_rate_per_m2: 55, print_min: 25, gallery_pct: 0.20, referrer_pct: 0.10 });
  const [quota, setQuota] = useState(null);

  const loadQuota = () => api.get("/artworks/quota").then((r) => setQuota(r.data)).catch(() => setQuota(null));
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    loadQuota();
  }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setUploading(true);
    try {
      setPreview(await createLightPreview(f));
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(data);
      // Default net_earnings per size: starts at 0, artist sets it
      const defaults = { S: 30, M: 60, L: 100, XL: 180, XXL: 280, XXXL: 400 };
      setSizes((data.suggested_sizes || []).map((s) => ({
        ...s,
        net_earnings: defaults[s.label] ?? 80,
        enabled: s.quality_ok && ["M", "L", "XL"].includes(s.label),
      })));
      setStandardFraming(buildStandardFramingOptions(data.width_px, data.height_px, []));
    } catch (err) {
      toast.error(formatApiError(err));
      setFile(null); setPreview(null);
    } finally { setUploading(false); }
  };

  const onContextFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (contextImages.length + files.length > 6) {
      toast.error("Máximo 6 imágenes de contexto");
      return;
    }
    setContextUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setContextImages((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} imagen(es) de contexto añadidas`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setContextUploading(false);
      e.target.value = ""; // reset to allow re-uploading same file
    }
  };

  const removeContextImage = (idx) => {
    setContextImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const breakdown = (s) => {
    const w = parseFloat(s.width_cm) || 0;
    const h = parseFloat(s.height_cm) || 0;
    const net = parseFloat(s.net_earnings) || 0;
    const cost = Math.max(settings.print_min, settings.print_rate_per_m2 * (w / 100) * (h / 100));
    const margin = 1 - settings.gallery_pct - settings.referrer_pct;
    const retail = margin > 0 ? (net + cost) / margin : 0;
    return { cost, retail };
  };

  const updateSize = (idx, field, value) => {
    setSizes((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: field === "enabled" ? value : (value === "" ? "" : parseFloat(value)) } : s)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!uploaded) { toast.error("Sube primero la imagen"); return; }
    const enabled = sizes.filter((s) => s.enabled);
    if (enabled.length === 0) { toast.error("Selecciona al menos una talla"); return; }
    setLoading(true);
    try {
      await api.post("/artworks", {
        title: form.title,
        description: form.description,
        category: form.category,
        image_path: uploaded.path,
        sizes: enabled.map((s) => ({
          label: s.label,
          width_cm: parseFloat(s.width_cm),
          height_cm: parseFloat(s.height_cm),
          net_earnings: parseFloat(s.net_earnings) || 0,
        })),
        standard_framing: standardFraming.map((o) => ({
          code: o.code,
          enabled: !!o.enabled,
          net_earnings: parseFloat(o.net_earnings) || 0,
          crop_x: parseFloat(o.crop_x ?? 0.5),
          crop_y: parseFloat(o.crop_y ?? 0.5),
        })),
        context_images: contextImages,
        max_impressions: editionMode === "limited" ? parseInt(maxImpressions) || null : null,
      });
      toast.success("Obra enviada para revisión");
      setForm({ title: "", description: "", category: "abstracto" });
      setFile(null); setPreview(null); setUploaded(null); setSizes([]); setStandardFraming([]); setContextImages([]);
      setEditionMode("open"); setMaxImpressions(25);
      loadQuota();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const inputCls = "w-full border border-neutral-300 px-3 py-2.5 outline-none focus:border-black bg-white text-sm";
  const previewRatio = uploaded ? `${uploaded.width_px} / ${uploaded.height_px}` : "4 / 5";

  return (
    <div data-testid="artist-upload">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-2">{t.artist.newWork}</h1>
      <p className="text-sm text-neutral-500 mb-6">{t.artist.uploadHint}</p>

      {quota && (
        <div className={`mb-6 p-4 border ${quota.remaining_week <= 2 || quota.remaining_pending <= 2 ? "bg-amber-50 border-amber-200" : "bg-neutral-50 border-neutral-200"}`} data-testid="quota-banner">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div>
              <div className="overline text-[10px] text-neutral-500">Esta semana</div>
              <div className="font-serif text-lg"><strong>{quota.uploaded_last_7_days}</strong> / {quota.max_uploads_per_week} subidas</div>
            </div>
            <div>
              <div className="overline text-[10px] text-neutral-500">Pendientes de revisar</div>
              <div className="font-serif text-lg"><strong>{quota.pending}</strong> / {quota.max_pending}</div>
            </div>
            <div className="text-xs text-neutral-600 flex-1 min-w-[200px]">
              Para mantener la curación de calidad de d-Pod Gallery, limitamos las subidas. Cuando el equipo revise tus obras pendientes, podrás subir más.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <input required placeholder={t.artist.title} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} py-3`} data-testid="art-title" />
          <textarea required placeholder={t.artist.description} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} h-28 py-3`} data-testid="art-description" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputCls} py-3`} data-testid="art-category">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <div>
            <label className="overline text-neutral-500 mb-2 block">1. Archivo de la obra (alta resolución)</label>
            <input type="file" accept="image/*" onChange={onFile} className="block w-full text-sm" data-testid="art-image" />
            {uploading && <p className="text-xs text-neutral-500 mt-2">Analizando dimensiones...</p>}
            {uploaded && (
              <p className="text-xs text-neutral-500 mt-2" data-testid="image-info">
                {uploaded.width_px}×{uploaded.height_px}px · {uploaded.orientation} · ratio {uploaded.aspect_ratio}
              </p>
            )}
          </div>

          {sizes.length > 0 && (
            <div className="border border-neutral-200 bg-white" data-testid="size-suggestions">
              <div className="px-4 py-3 border-b border-neutral-200">
                <div className="overline text-neutral-700">2. Tallas a ofrecer (proporciones reales)</div>
                <p className="text-xs text-neutral-500 mt-1">Marca las que quieres vender. Calidad mínima 200 DPI para fine art.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-neutral-50">
                      <th className="text-left px-3 py-2 overline text-neutral-500">✓</th>
                      <th className="text-left px-3 py-2 overline text-neutral-500">Talla</th>
                      <th className="text-left px-3 py-2 overline text-neutral-500">Medidas</th>
                      <th className="text-left px-3 py-2 overline text-neutral-500">Calidad</th>
                      <th className="text-left px-3 py-2 overline text-neutral-500">Tu neto (€)</th>
                      <th className="text-left px-3 py-2 overline text-neutral-500">Coste</th>
                      <th className="text-left px-3 py-2 overline text-neutral-500">PVP final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sizes.map((s, idx) => {
                      const b = breakdown(s);
                      const dim = `${s.width_cm}×${s.height_cm}cm`;
                      return (
                        <tr key={idx} className={`border-t border-neutral-100 ${!s.quality_ok ? "opacity-50" : ""}`} data-testid={`size-row-${s.label}`}>
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={s.enabled} disabled={!s.quality_ok} onChange={(e) => updateSize(idx, "enabled", e.target.checked)} className="w-4 h-4" data-testid={`size-enable-${s.label}`} />
                          </td>
                          <td className="px-3 py-2 font-medium">{s.label}</td>
                          <td className="px-3 py-2 text-neutral-700">{dim}</td>
                          <td className="px-3 py-2">
                            {s.quality_ok ? (
                              <span className="text-emerald-700 text-xs">{s.dpi} DPI</span>
                            ) : (
                              <span className="text-red-600 text-xs">{s.dpi} DPI · baja</span>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            <input type="number" min="0" step="0.01" value={s.net_earnings} disabled={!s.enabled} onChange={(e) => updateSize(idx, "net_earnings", e.target.value)} className="w-20 border border-neutral-200 px-2 py-1.5 text-xs disabled:bg-neutral-50" data-testid={`size-net-${s.label}`} />
                          </td>
                          <td className="px-3 py-2 text-neutral-600">€{b.cost.toFixed(2)}</td>
                          <td className="px-3 py-2 font-serif text-base" data-testid={`size-retail-${s.label}`}>€{b.retail.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-neutral-200 bg-neutral-50 text-xs text-neutral-600 leading-relaxed">
                {t.artist.breakdownHint
                  .replace("{rate}", settings.print_rate_per_m2)
                  .replace("{min}", settings.print_min)
                  .replace("{gallery}", (settings.gallery_pct * 100).toFixed(0))
                  .replace("{ref}", (settings.referrer_pct * 100).toFixed(0))}
              </div>
            </div>
          )}

          {standardFraming.length > 0 && (
            <StandardFramingConfigurator
              imageSrc={preview}
              options={standardFraming}
              onChange={setStandardFraming}
              settings={settings}
              testPrefix="upload-standard-framing"
            />
          )}

          <div className="border border-neutral-200 bg-white" data-testid="edition-section">
            <div className="px-4 py-3 border-b border-neutral-200">
              <div className="overline text-neutral-700">4. Edición · Número de impresiones</div>
              <p className="text-xs text-neutral-500 mt-1">
                Cuantas menos impresiones permitas, más exclusiva y valiosa será tu obra. Al aprobar, te pediremos tantas tarjetas/certificados firmados como impresiones haya en la edición.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <label className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${editionMode === "open" ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`} data-testid="edition-open">
                <input type="radio" checked={editionMode === "open"} onChange={() => setEditionMode("open")} className="mt-1" />
                <div>
                  <div className="text-sm font-medium">Edición abierta · sin límite</div>
                  <div className="text-xs text-neutral-500 mt-0.5">La obra se podrá imprimir indefinidamente. Mejor para obras populares o gran formato editorial.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${editionMode === "limited" ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`} data-testid="edition-limited">
                <input type="radio" checked={editionMode === "limited"} onChange={() => setEditionMode("limited")} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Edición limitada</div>
                  <div className="text-xs text-neutral-500 mt-0.5 mb-2">Cuando se vendan todas las impresiones, la obra se retira automáticamente. Más rara = más valor.</div>
                  {editionMode === "limited" && (
                    <div className="flex items-center gap-3 mt-3">
                      <input
                        type="number"
                        min="1"
                        max="999"
                        value={maxImpressions}
                        onChange={(e) => setMaxImpressions(e.target.value)}
                        className="w-24 border border-neutral-300 px-3 py-2 text-sm"
                        data-testid="max-impressions-input"
                      />
                      <span className="text-xs text-neutral-500">impresiones (1–999)</span>
                      <div className="flex gap-1 ml-2">
                        {[5, 10, 25, 50, 100].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setMaxImpressions(n)}
                            className={`overline text-[10px] px-2 py-1 border ${parseInt(maxImpressions) === n ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-50"}`}
                            data-testid={`preset-${n}`}
                          >{n}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>

          <div className="border border-neutral-200 bg-white" data-testid="context-images-section">
            <div className="px-4 py-3 border-b border-neutral-200">
              <div className="overline text-neutral-700">5. Imágenes de contexto (opcional)</div>
              <p className="text-xs text-neutral-500 mt-1">
                Sube fotos de tu obra colgada en una pared real: casa, sala de espera, oficina, hotel… Ayuda al comprador a imaginarla en su espacio. Máximo 6 imágenes.
              </p>
            </div>
            <div className="p-4 space-y-3">
              {contextImages.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="context-images-grid">
                  {contextImages.map((path, idx) => (
                    <div key={idx} className="relative group aspect-[4/3] bg-neutral-100 overflow-hidden border border-neutral-200" data-testid={`context-img-${idx}`}>
                      <img src={fileUrl(path)} alt={`Contexto ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeContextImage(idx)}
                        className="absolute top-1 right-1 bg-black/80 text-white text-xs w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-context-${idx}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {contextImages.length < 6 && (
                <div>
                  <label className="inline-block cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onContextFiles}
                      className="hidden"
                      disabled={contextUploading}
                      data-testid="context-image-input"
                    />
                    <span className="inline-block overline text-xs px-4 py-2 border border-neutral-300 hover:bg-neutral-50 cursor-pointer">
                      {contextUploading ? "Subiendo..." : `+ Añadir imágenes (${contextImages.length}/6)`}
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <button disabled={loading || !uploaded} type="submit" className="bg-black text-white px-10 py-4 overline hover:bg-neutral-800 transition-colors disabled:opacity-50" data-testid="art-submit">
            {loading ? "..." : t.artist.submit}
          </button>
        </div>
        <div>
          <div className="overline text-neutral-500 mb-3">Vista previa (proporción real)</div>
          <div style={{ aspectRatio: previewRatio }} className="bg-neutral-100 flex items-center justify-center overflow-hidden border border-neutral-200 max-h-[80vh]">
            {preview ? <img src={preview} alt="preview" className="w-full h-full object-contain" /> : <div className="overline text-neutral-400">Sube una imagen</div>}
          </div>
        </div>
      </form>
    </div>
  );
}

export default function ArtistDashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="min-h-screen flex items-center justify-center overline text-neutral-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "artist") return <Navigate to="/" replace />;

  const links = [
    { to: "/artist", end: true, label: t.artist.overview, key: "overview" },
    { to: "/artist/works", label: t.artist.works, key: "works" },
    { to: "/artist/upload", label: t.artist.upload, key: "upload" },
    { to: "/artist/profile", label: t.artist.profile, key: "profile" },
  ];

  return (
    <Routes>
      <Route element={<DashboardLayout role="artist" links={links} />}>
        <Route index element={<Overview />} />
        <Route path="works" element={<MyWorks />} />
        <Route path="upload" element={<Upload />} />
        <Route path="profile" element={<ProfileEditor />} />
      </Route>
    </Routes>
  );
}
